import { useCallback, useRef, useState } from "react"
import { trackAssistantEvent } from "../utils/assistant-analytics"
import {
  getStaffConversation,
  StaffFeedbackReason,
  sendStaffChatMessageStream
} from "../../api/staff-agent"

export interface StaffToolEvent {
  name: string
  running: boolean
  ok?: boolean
  rowCount?: number
}

export interface StaffPendingAction {
  id: string
  actionType: string
  summary: string
  payload: Record<string, unknown>
  status: string
}

export interface StaffFormField {
  name: string
  label: string
  type:
    | "text"
    | "textarea"
    | "number"
    | "email"
    | "date"
    | "select"
    | "multiselect"
    | "checkbox"
  required?: boolean
  placeholder?: string
  helpText?: string
  options?: { value: string; label: string }[]
  defaultValue?: string | number | boolean | string[]
}

export interface StaffFormRequest {
  id: string
  title: string
  description?: string
  submitLabel?: string
  fields: StaffFormField[]
  /** Set client-side once the user submits, to lock the card */
  submitted?: boolean
}

export interface StaffChatMessage {
  id: string
  role: "user" | "assistant"
  content: string
  toolEvents: StaffToolEvent[]
  pendingActions: StaffPendingAction[]
  formRequests: StaffFormRequest[]
  /** Follow-up question chips for the finished turn (optional, best-effort) */
  suggestions?: string[]
  feedbackTargetId?: string
  feedback?: {
    sentiment: "helpful" | "not_helpful"
    reasons: StaffFeedbackReason[]
  }
  error?: string
}

interface SSEEvent {
  event: string
  data: any
}

/**
 * Splits complete SSE blocks ("event: x\ndata: {...}\n\n") off the front of
 * the buffer, returning parsed events plus the incomplete remainder.
 */
function parseSSEBlocks(buffer: string): { events: SSEEvent[]; rest: string } {
  const events: SSEEvent[] = []
  const blocks = buffer.split(/\r?\n\r?\n/)
  const rest = blocks.pop() ?? ""
  for (const block of blocks) {
    let event = "message"
    let data = ""
    for (const line of block.split(/\r?\n/)) {
      if (line.startsWith("event:")) event = line.slice(6).trim()
      else if (line.startsWith("data:")) data += line.slice(5).trim()
    }
    if (!data) continue
    try {
      events.push({ event, data: JSON.parse(data) })
    } catch {
      /* skip malformed block */
    }
  }
  return { events, rest }
}

let idCounter = 0
const genId = () => `staff-msg-${++idCounter}`

/**
 * Streaming chat against bb-admin-agent. Plain hook (not RTK Query) because
 * the transport is a POST-based SSE stream read incrementally with fetch.
 */
export function useStaffChatStream() {
  const [messages, setMessages] = useState<StaffChatMessage[]>([])
  const [isStreaming, setIsStreaming] = useState(false)
  // Active session id: ref is the in-flight source of truth (avoids stale
  // closures in sendMessage), state mirrors it for UI highlighting.
  const [conversationId, setConversationId] = useState<string | undefined>(
    undefined
  )
  const conversationIdRef = useRef<string | undefined>(undefined)
  const abortRef = useRef<AbortController | null>(null)
  const lastRequestRef = useRef<{ text: string; page?: string } | null>(null)

  const patchLastAssistant = useCallback(
    (patch: (m: StaffChatMessage) => StaffChatMessage) => {
      setMessages(prev => {
        const next = [...prev]
        for (let i = next.length - 1; i >= 0; i--) {
          if (next[i].role === "assistant") {
            next[i] = patch(next[i])
            break
          }
        }
        return next
      })
    },
    []
  )

  const handleEvent = useCallback(
    ({ event, data }: SSEEvent) => {
      switch (event) {
        case "text":
          patchLastAssistant(m => ({
            ...m,
            content: m.content + (data.content ?? "")
          }))
          break
        case "tool_call":
          patchLastAssistant(m => ({
            ...m,
            toolEvents: [...m.toolEvents, { name: data.name, running: true }]
          }))
          break
        case "tool_result":
          patchLastAssistant(m => {
            const toolEvents = [...m.toolEvents]
            const idx = toolEvents.findIndex(
              t => t.running && t.name === data.name
            )
            if (idx >= 0) {
              toolEvents[idx] = {
                name: data.name,
                running: false,
                ok: data.ok,
                rowCount: data.rowCount
              }
            }
            const pendingActions = data.pending_action
              ? [...m.pendingActions, data.pending_action]
              : m.pendingActions
            const formRequests = data.form_request
              ? [...m.formRequests, data.form_request]
              : m.formRequests
            return { ...m, toolEvents, pendingActions, formRequests }
          })
          break
        case "suggestions":
          patchLastAssistant(m => ({
            ...m,
            suggestions: Array.isArray(data.items)
              ? data.items.filter((s: unknown) => typeof s === "string")
              : undefined
          }))
          break
        case "done":
          trackAssistantEvent("response_received")
          if (data.conversationId) {
            conversationIdRef.current = data.conversationId
            setConversationId(data.conversationId)
          }
          if (data.turnId) {
            patchLastAssistant(m => ({ ...m, feedbackTargetId: data.turnId }))
          }
          break
        case "error":
          trackAssistantEvent("response_error")
          patchLastAssistant(m => ({
            ...m,
            error: data.message || "The agent hit an error"
          }))
          break
      }
    },
    [patchLastAssistant]
  )

  const sendMessage = useCallback(
    async (text: string, page?: string) => {
      const trimmed = text.trim()
      if (!trimmed) return
      lastRequestRef.current = { text: trimmed, page }

      setMessages(prev => [
        ...prev,
        {
          id: genId(),
          role: "user",
          content: trimmed,
          toolEvents: [],
          pendingActions: [],
          formRequests: []
        },
        {
          id: genId(),
          role: "assistant",
          content: "",
          toolEvents: [],
          pendingActions: [],
          formRequests: []
        }
      ])
      setIsStreaming(true)
      const abort = new AbortController()
      abortRef.current = abort

      try {
        const res = await sendStaffChatMessageStream(
          trimmed,
          conversationIdRef.current,
          page,
          abort.signal
        )
        const reader = res.body!.getReader()
        const decoder = new TextDecoder()
        let buffer = ""
        for (;;) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const { events, rest } = parseSSEBlocks(buffer)
          buffer = rest
          events.forEach(handleEvent)
        }
      } catch (error: any) {
        if (error?.name !== "AbortError") {
          patchLastAssistant(m => ({
            ...m,
            error: error?.message || "Something went wrong"
          }))
        }
      } finally {
        setIsStreaming(false)
        abortRef.current = null
      }
    },
    [handleEvent, patchLastAssistant]
  )

  const stop = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  const retryLast = useCallback(() => {
    const request = lastRequestRef.current
    if (!request || isStreaming) return
    // Failed turns are not persisted by the service. Remove their local user
    // and assistant rows before replaying so retry does not visually duplicate
    // the question.
    setMessages(prev => {
      const assistantIndex = prev.length - 1
      const userIndex = assistantIndex - 1
      if (
        assistantIndex >= 0 &&
        prev[assistantIndex].role === "assistant" &&
        prev[assistantIndex].error &&
        userIndex >= 0 &&
        prev[userIndex].role === "user"
      ) {
        return prev.slice(0, userIndex)
      }
      return prev
    })
    sendMessage(request.text, request.page)
  }, [isStreaming, sendMessage])

  const resetConversation = useCallback(() => {
    abortRef.current?.abort()
    conversationIdRef.current = undefined
    setConversationId(undefined)
    setMessages([])
    lastRequestRef.current = null
  }, [])

  /**
   * Load a persisted session into the panel and continue chatting in it.
   * Tool chips / approval cards aren't persisted per-message, so history
   * renders as plain markdown messages.
   */
  const loadConversation = useCallback(async (id: string) => {
    abortRef.current?.abort()
    const detail = await getStaffConversation(id)
    conversationIdRef.current = id
    lastRequestRef.current = null
    setConversationId(id)
    setMessages(
      detail.messages.map(m => ({
        id: genId(),
        role: m.role,
        content: m.content,
        feedbackTargetId: m.turnId,
        feedback: m.feedback,
        toolEvents: [],
        pendingActions: [],
        formRequests: []
      }))
    )
  }, [])

  /** Lock a form card after the user submits it. */
  const markFormSubmitted = useCallback((formId: string) => {
    setMessages(prev =>
      prev.map(m =>
        m.formRequests.some(f => f.id === formId)
          ? {
              ...m,
              formRequests: m.formRequests.map(f =>
                f.id === formId ? { ...f, submitted: true } : f
              )
            }
          : m
      )
    )
  }, [])

  /** Reflect approve/reject results back into the rendered approval cards. */
  const updatePendingActionStatus = useCallback(
    (actionId: string, status: string) => {
      setMessages(prev =>
        prev.map(m =>
          m.pendingActions.some(a => a.id === actionId)
            ? {
                ...m,
                pendingActions: m.pendingActions.map(a =>
                  a.id === actionId ? { ...a, status } : a
                )
              }
            : m
        )
      )
    },
    []
  )

  return {
    messages,
    isStreaming,
    conversationId,
    sendMessage,
    stop,
    retryLast,
    resetConversation,
    loadConversation,
    markFormSubmitted,
    updatePendingActionStatus
  }
}
